/**
 * Tactical audio and haptic feedback module.
 *
 * Provides "eyes-on-the-road" situational telemetry cues via Web Audio API
 * and Web Vibration API for hands-free field operations.
 */

const AUDIO_STORAGE_KEY = 'noderadar_audio_feedback';
const HAPTIC_STORAGE_KEY = 'noderadar_haptic_feedback';

interface WindowWithWebkitAudio extends Window {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
}

export class FeedbackManager {
  private audioCtx: AudioContext | null = null;
  private audioEnabled = false;
  private hapticEnabled = false;

  constructor() {
    if (typeof localStorage !== 'undefined') {
      this.audioEnabled = localStorage.getItem(AUDIO_STORAGE_KEY) === 'true';
      this.hapticEnabled = localStorage.getItem(HAPTIC_STORAGE_KEY) === 'true';
    }
  }

  /**
   * Lazily initializes the browser AudioContext upon user gesture.
   *
   * Why: Modern browsers require an active user interaction before activating
   * the Web Audio context, preventing auto-play policy rejections.
   */
  private initAudioContext(): AudioContext | null {
    if (!this.audioCtx && typeof window !== 'undefined') {
      const win = window as WindowWithWebkitAudio;
      const AudioCtxClass = win.AudioContext || win.webkitAudioContext;
      if (AudioCtxClass) {
        this.audioCtx = new AudioCtxClass();
      }
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }
    return this.audioCtx;
  }

  /**
   * Emits an audible frequency pulse modulated by LoRa signal quality (SNR).
   *
   * Why: Allows operators driving or cycling to perceive signal strength variations
   * without taking eyes off the road. Higher SNR produces higher frequency tones.
   *
   * @param snr - Signal to noise ratio in dB
   */
  playPacketTone(snr: number): void {
    if (!this.audioEnabled) return;

    const ctx = this.initAudioContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      // Clamp SNR between -20 and +12 dB and map to frequency 320 Hz - 880 Hz
      const clampedSnr = Math.max(-20, Math.min(12, snr));
      const freq = 320 + ((clampedSnr + 20) / 32) * 560;

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now);

      // Smooth attack and decay to prevent acoustic clicks
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.12, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.085);
    } catch (err) {
      console.warn('[Feedback] Audio tone failure:', err);
    }
  }

  /**
   * Emits an ascending tactical acoustic chime when a new station is discovered.
   *
   * Why: Alerts the operator that a previously unrecorded Meshtastic node has entered reception range.
   */
  playNewNodeChime(): void {
    if (!this.audioEnabled) return;

    const ctx = this.initAudioContext();
    if (!ctx) return;

    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(523.25, now); // C5
      osc.frequency.setValueAtTime(659.25, now + 0.06); // E5
      osc.frequency.setValueAtTime(783.99, now + 0.12); // G5

      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.18, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.23);
    } catch (err) {
      console.warn('[Feedback] New node chime failure:', err);
    }
  }

  /**
   * Triggers a subtle tactile pulse on vibration-capable devices.
   *
   * Why: Provides silent tactile notification during field operations.
   *
   * @param pattern - Duration in ms or vibration cadence array
   */
  vibrate(pattern: number | number[]): void {
    if (!this.hapticEnabled) return;
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
      } catch (err) {
        console.warn('[Feedback] Vibration failure:', err);
      }
    }
  }

  isAudioEnabled(): boolean {
    return this.audioEnabled;
  }

  setAudioEnabled(enabled: boolean): void {
    this.audioEnabled = enabled;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(AUDIO_STORAGE_KEY, String(enabled));
    }
    if (enabled) {
      this.playPacketTone(5);
    }
  }

  isHapticEnabled(): boolean {
    return this.hapticEnabled;
  }

  setHapticEnabled(enabled: boolean): void {
    this.hapticEnabled = enabled;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(HAPTIC_STORAGE_KEY, String(enabled));
    }
    if (enabled) {
      this.vibrate(40);
    }
  }
}
